import { ButtonUnstyled } from '@sketch/components'
import styled, { css, DefaultTheme } from 'styled-components'

import { ReactComponent as Arrow } from '@sketch/icons/arrow-circle-right-14'

// Hide the artboard name if the artboard's width is less than:
const HEADER_MIN_WIDTH = 100

export const ArrowIcon = styled(Arrow)`
  width: 14px;
  height: 14px;
  pointer-events: none;
  flex-shrink: 0;
`

export const VariantPropertyContainer = styled.span<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px; /* stylelint-disable-line scales/space */
  width: 100%;
  height: 17px;
  overflow: hidden;
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`

export const VariantPropertyLabel = styled.span`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  box-sizing: border-box;
  max-width: 100%;
  height: 17px;
  padding: 0 5px; /* stylelint-disable-line scales/space */
  border-radius: 3px;
  background-color: ${({ theme }) => theme.colors.background.tertiary.C};
  color: ${({ theme }) => theme.colors.foreground.secondary.B};
  overflow: hidden;
`

export const VariantPropertyText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const VariantBooleanIcon = styled.span`
  display: flex;
  flex: 0 0 auto;
  margin-left: 2px; /* stylelint-disable-line scales/space */

  svg {
    width: 12px;
    height: 12px;
  }
`

export const VariantPropertySummaryItem = styled.span`
  display: inline-flex;
  align-items: center;
`

export const ArtboardHeader = styled.div`
  position: absolute;
  top: 1px;

  height: auto;
  transform: translateY(-100%);

  /* Allow the ArtboardCTA to have the correct measure of ArtboardHeader */
  width: 100%;

  /*
  This is a mimic way of hiding a element based on its size via CSS
  basically we if 100% - the minimum with is negative then the max-width is 0px
  so this part is hidden
  */
  max-width: max(0px, calc((100% - ${HEADER_MIN_WIDTH}px) * 999));
  overflow: hidden;
  margin-left: -1px; /* stylelint-disable-line scales/space */
`

export const ArtboardName = styled.span`
  position: relative;
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  user-select: none;
  pointer-events: auto;
`

export const ArtboardCTA = styled(ButtonUnstyled)(
  ({ theme }) => css`
    pointer-events: auto;
    line-height: ${theme.lineHeights.default};
    color: currentColor;

    display: inline-flex;
    align-items: center;
    gap: 2px; /* stylelint-disable-line scales/space */

    max-width: calc(100% - 6px);

    padding: 4px; /* stylelint-disable-line scales/space */
    border-radius: 4px 4px 0 0;
    position: relative;

    /**
     * The CTA takes focus when its entity is selected, so that Enter opens it
     * (see "useFocusCtaWhenSelected"). That focus is a side effect of selecting,
     * not of the user navigating to a button: the selection already draws its
     * own chrome, and the CTA is not tab-reachable, so a focus ring here would
     * read as a second, unexplained highlight on the frame name.
     */
    :focus,
    :focus-visible {
      outline: none;
    }

    ::after {
      content: '';
      display: block;
      visibility: hidden;

      position: absolute;

      right: -4px;
      bottom: -2px;

      width: 6px;
      height: 6px;

      background: radial-gradient(
          circle 4px at top right,
          #0000 98%,
          ${theme.colors.sketch.A}
        )
        bottom left;
    }
  `
)

// Returns the entity's outline/accent color: components and the Sections that
// group their variants both take the symbol accent; everything else - including
// plain sections - uses the default artboard colour so sections read like
// regular frames.
const getEntityAccentColor = ({
  theme,
  $isComponent,
  $isVariantSet,
}: {
  theme: DefaultTheme
  $isComponent?: boolean
  $isVariantSet?: boolean
}) => {
  if ($isComponent || $isVariantSet) return theme.colors.inspect.symbol.A
  return theme.colors.sketch.A
}

/**
 * The dashed outline marking out a Section's bounds. Sections carry no default
 * style, so without this an unstyled one would leave no mark on the canvas at
 * all.
 *
 * Drawn as an SVG rather than a CSS border to match the Mac app's stroke: 1px
 * centred on the Section bounds (SVG strokes straddle the path, hence the
 * `overflow: visible` letting the outer half paint outside the container), 6px
 * dashes, butt caps and round joins. It lives in the DOM overlay layer, which
 * is positioned but never scaled, so the stroke stays 1px at any zoom.
 *
 * The Mac app's spec calls for a flat #808080. We take the nearest thing the
 * palette offers instead, which lands close in both themes once composited over
 * the canvas - #6e6e6e in light, #939393 in dark - and, unlike a fixed grey,
 * keeps its contrast against a canvas background that is itself a theme colour.
 */
export const SectionOutline = styled.svg`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  overflow: visible;
  pointer-events: none;

  rect {
    fill: none;
    stroke: ${({ theme }) => theme.colors.foreground.secondary.D};
    stroke-width: 1;
    /* 6 on, 6 off - the dash length we were given, with the gap Sketch's own
    borderOptions imply for a dashPattern of [6] */
    stroke-dasharray: 6 6;
    stroke-linecap: butt;
    stroke-linejoin: round;
  }
`

export const FrameOverlayContainer = styled.div<{
  $disableHover?: boolean
  $isComponent?: boolean
  $isVariantSet?: boolean
}>`
  position: absolute;
  box-sizing: border-box;

  /* Just some default font styles */
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.333; /* stylelint-disable-line scales/line-heights */

  color: ${({ theme }) => theme.colors.foreground.secondary.C};

  ${({ $isComponent, $isVariantSet, theme }) =>
    ($isComponent || $isVariantSet) &&
    css`
      color: ${getEntityAccentColor({ theme, $isComponent, $isVariantSet })};

      ${ArtboardCTA} {
        &::after {
          background: radial-gradient(
              circle 4px at top right,
              #0000 98%,
              ${getEntityAccentColor({ theme, $isComponent, $isVariantSet })}
            )
            bottom left;
        }
      }
    `}

  ${ArrowIcon} {
    display: none;
  }

  ${({ $disableHover, ...props }) =>
    !$disableHover &&
    css`
      &:hover {
        box-shadow: inset 0 0 0 1px ${getEntityAccentColor(props)};

        /* The accent outline sits on the same edge - showing both would read as
        a single grubby line */
        ${SectionOutline} {
          display: none;
        }
      }
    `}

  &[aria-selected='true'] {
    box-shadow:
      0 0 0 1px ${getEntityAccentColor},
      inset 0 0 0 1px ${getEntityAccentColor};

    color: white;
    font-weight: 500;

    ${SectionOutline} {
      display: none;
    }

    ${ArtboardCTA} {
      background-color: ${getEntityAccentColor};

      ::after {
        visibility: visible;
      }
    }

    ${ArrowIcon} {
      display: flex;
    }
  }
`

export const FrameContentOverlay = styled.div`
  width: 100%;
  height: 100%;
`
